// Client Portal — Video Studio portal. Extracted from App.js.
import { useState, useEffect } from "react";
import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Download,
  Edit3,
  Image,
  Loader2,
  Mic,
  Music,
  Play,
  Plus,
  Share2,
  Sparkles,
  Subtitles,
  Trash2,
} from "lucide-react";
import { authFetch, authUpload } from "../lib/api";
import { showToast } from "../lib/toast";
import { SocialShareDialog } from "../components/SocialShareDialog";
import {
  PodcastField,
  PodcastModal,
  podcastErr,
  fetchPodcastAudioUrl,
  podcastInputCls,
} from "../components/podcastUi";

const VIDEO_BUSY_STATUSES = ["queued", "rendering", "assembling", "previewing"];
const VIDEO_AESTHETICS = ["photoreal", "oil_portrait", "engraving"];

// Mirror of video-gen/client/shotlist.py — that validator is the source of
// truth (the server re-validates every save); these are the editor's options
// and client-side guards so an obvious mistake doesn't need a round trip.
const VIDEO_ANGLES = [
  "front",
  "close_up",
  "wide",
  "low_angle",
  "high_angle",
  "three_quarter_left",
  "three_quarter_right",
];
const VIDEO_MOVES = [
  "static",
  "pan_left",
  "pan_right",
  "pan_up",
  "pan_down",
  "zoom_in",
  "zoom_out",
];
// Mirror of video-gen/client/formats.py + migration 146's CHECK constraints.
const VIDEO_ASPECTS = [
  { id: "9:16", label: "9:16 vertical", hint: "Reels, TikTok, Shorts" },
  { id: "16:9", label: "16:9 widescreen", hint: "YouTube, web embed" },
  { id: "1:1", label: "1:1 square", hint: "Feed posts" },
  { id: "4:5", label: "4:5 portrait", hint: "Instagram feed" },
];
const VIDEO_RESOLUTIONS = ["720p", "1080p"];
const VIDEO_LOGO_POSITIONS = [
  "bottom_right",
  "bottom_left",
  "top_right",
  "top_left",
];
const VIDEO_TARGET_SECONDS = [30, 45, 60, 75, 90, 120, 180];

// Mirror of shotlist.GENERATION_MODES (item 48). The default is i2v because
// 'camera' is what made every b-roll shot a panned still.
const VIDEO_GENERATION_MODES = [
  {
    id: "i2v",
    label: "Motion from keyframe",
    hint: "Generates a still, then animates it — the subject actually moves",
  },
  {
    id: "t2v",
    label: "Motion from text",
    hint: "Straight to video, no keyframe. No storyboard preview for this shot",
  },
  {
    id: "camera",
    label: "Camera move only",
    hint: "Parametric camera move over a still scene — nothing in frame moves",
  },
];
const VIDEO_DEFAULT_MODE = "i2v";
const VIDEO_MAX_MOTION = 300;

const VIDEO_MAX_SHOTS = 20;
const VIDEO_MAX_SCENE_DESC = 500;
const VIDEO_MAX_DIALOGUE = 1200;
const VIDEO_BROLL_DURATION = [2, 5]; // one Wan A14B generation (item 49)

// Same checks as validate_shotlist, in the same order, so the inline message
// matches what the server would say. Returns an error string or null.
const validateShotsLocal = (shots, { hasVoiceover = false } = {}) => {
  if (!shots.length) return "A shot list needs at least one shot.";
  if (shots.length > VIDEO_MAX_SHOTS)
    return `Too many shots (max ${VIDEO_MAX_SHOTS}).`;
  for (let i = 0; i < shots.length; i += 1) {
    const s = shots[i];
    const n = i + 1;
    if (!(s.scene_desc || "").trim()) return `Shot ${n}: scene needs a description.`;
    if ((s.scene_desc || "").length > VIDEO_MAX_SCENE_DESC)
      return `Shot ${n}: scene description is too long (max ${VIDEO_MAX_SCENE_DESC}).`;
    if (s.type === "talking_head") {
      if (!(s.dialogue || "").trim())
        return `Shot ${n}: a talking head needs dialogue.`;
      if ((s.dialogue || "").length > VIDEO_MAX_DIALOGUE)
        return `Shot ${n}: dialogue is too long (max ${VIDEO_MAX_DIALOGUE}).`;
      if (!(s.avatar_ref || "").trim())
        return `Shot ${n}: a talking head needs an avatar.`;
    } else {
      const [lo, hi] = VIDEO_BROLL_DURATION;
      const d = Number(s.duration_seconds);
      if (!Number.isFinite(d) || d < lo || d > hi)
        return `Shot ${n}: b-roll length must be ${lo}-${hi} seconds.`;
      const mode = s.generation_mode || VIDEO_DEFAULT_MODE;
      if (!VIDEO_GENERATION_MODES.some((m) => m.id === mode))
        return `Shot ${n}: unknown generation mode.`;
      if (mode !== "camera" && !(s.motion || "").trim())
        return `Shot ${n}: describe what moves, or switch it to "Camera move only".`;
      if ((s.motion || "").length > VIDEO_MAX_MOTION)
        return `Shot ${n}: motion description is too long (max ${VIDEO_MAX_MOTION}).`;
    }
  }
  const talkingHeads = shots.filter((s) => s.type === "talking_head").length;
  // Item 26: b-roll only is fine when a voiceover carries the audio.
  if (talkingHeads === 0 && !hasVoiceover)
    return "Add a talking head, or upload a voiceover so a b-roll-only video has audio.";
  // Item 27: one file can't be split across several speakers.
  if (talkingHeads > 1 && hasVoiceover)
    return "A voiceover can only cover one talking head. Remove the extra talking heads, or drop the voiceover to use text-to-speech.";
  return null;
};

const VIDEO_JOB_KIND_LABELS = {
  still: "Keyframe still",
  angle: "Angle still",
  talking_head: "Talking head",
  broll: "B-roll",
  upscale: "Upscale pass",
};

// "2:41" from GPU seconds
const fmtGpuTime = (sec) => {
  const s = Math.round(Number(sec) || 0);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

const videoShots = (p) => {
  try {
    const raw =
      typeof p.shot_list === "string" ? JSON.parse(p.shot_list) : p.shot_list;
    return Array.isArray(raw) ? raw : [];
  } catch {
    return [];
  }
};

// Mockup-style param pill (video-create-flow.html .param)
const VideoParam = ({ children }) => (
  <span className="text-[11px] text-slate-600 bg-slate-100 border border-slate-200 rounded-full px-2.5 py-0.5 tabular-nums">
    {children}
  </span>
);

const VideoKindBadge = ({ type }) => (
  <span
    className={`text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded text-white ${
      type === "talking_head" ? "bg-slate-900" : "bg-slate-600"
    }`}
  >
    {type === "talking_head" ? "Talk" : "B-roll"}
  </span>
);

// Production card (video-production-card.html): shot timeline sized by
const VIDEO_IMAGE_KINDS = ["still", "angle"];

// Lazy authed preview of one shot artifact (inspector keyframe→clip chain).
// Fetches the file as a blob (plain src can't send the JWT); a 404 (not
// rendered yet / legacy row) renders nothing rather than a broken box.
const VideoShotArtifact = ({ projectId, shotIndex, kind, take }) => {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let objectUrl = null;
    let cancelled = false;
    (async () => {
      try {
        objectUrl = await fetchPodcastAudioUrl(
          `/video/projects/${projectId}/shots/${shotIndex}/artifact?kind=${kind}` +
            (take == null ? "" : `&take=${take}`),
        );
        if (cancelled) URL.revokeObjectURL(objectUrl);
        else setUrl(objectUrl);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
      objectUrl && URL.revokeObjectURL(objectUrl);
    };
  }, [projectId, shotIndex, kind, take]);
  if (failed) return null;
  if (!url)
    return <div className="w-28 h-16 rounded bg-slate-100 animate-pulse" />;
  return VIDEO_IMAGE_KINDS.includes(kind) ? (
    <img
      src={url}
      alt={kind}
      className="h-16 rounded border border-slate-200 object-cover"
    />
  ) : (
    <video
      controls
      src={url}
      className="h-24 rounded border border-slate-200"
    />
  );
};

// duration, per-shot render queue from video_jobs, inspector for the selected
// shot. Everything shown comes from the project row + its jobs — no
// placeholders. The `actions` node carries the existing status-driven buttons.
const VideoProductionCard = ({
  project: p,
  statusChip,
  actions,
  onRegenerateShot,
  regenBusy,
  onTakeSwitched,
}) => {
  const [sel, setSel] = useState(0);
  const shots = videoShots(p);
  const jobs = p.jobs || [];
  const shot = shots[sel];

  const mix = (() => {
    try {
      return typeof p.mix_manifest === "string"
        ? JSON.parse(p.mix_manifest)
        : p.mix_manifest;
    } catch {
      return null;
    }
  })();

  const totalSec = shots.reduce(
    (a, s) => a + (Number(s.duration_seconds) || 0),
    0,
  );
  const renderedSec = jobs.reduce(
    (a, j) => a + (Number(j.gpu_seconds) || 0),
    0,
  );

  // Takes (item 28): a shot's state is its NEWEST take's state — an earlier
  // take that failed or was superseded must not colour the timeline.
  const latestTake = (i) =>
    jobs.reduce(
      (max, j) =>
        j.shot_index === i ? Math.max(max, Number(j.take_index) || 0) : max,
      0,
    );

  const shotStatus = (i) => {
    const take = latestTake(i);
    const js = jobs.filter(
      (j) => j.shot_index === i && (Number(j.take_index) || 0) === take,
    );
    if (!js.length) return null;
    if (js.some((j) => j.status === "failed")) return "failed";
    if (js.some((j) => ["running", "submitted"].includes(j.status)))
      return "running";
    if (js.every((j) => ["done", "skipped"].includes(j.status))) return "done";
    return "pending";
  };

  const jobDot = (status) =>
    ({
      done: "bg-green-500",
      running: "bg-amber-400 animate-pulse",
      submitted: "bg-amber-400 animate-pulse",
      failed: "bg-red-500",
      skipped: "bg-slate-300",
    })[status] || "bg-slate-300";

  // Live sampling steps (118) drive a real bar; otherwise status-shaped.
  const jobBar = (j) => {
    if (["running", "submitted"].includes(j.status) && j.step_total) {
      const pct = Math.min(
        100,
        Math.round(((j.step_current || 0) / j.step_total) * 100),
      );
      return [`${pct}%`, "bg-amber-400"];
    }
    return (
      {
        done: ["100%", "bg-green-500"],
        running: ["60%", "bg-amber-400 animate-pulse"],
        submitted: ["15%", "bg-amber-400"],
        failed: ["100%", "bg-red-400"],
      }[j.status] || ["0%", "bg-slate-300"]
    );
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      {/* head */}
      <div className="flex items-start justify-between gap-3 p-4 pb-3">
        <div className="min-w-0">
          <h4 className="text-sm font-bold text-slate-900 truncate">
            {p.title}
          </h4>
          <p className="text-xs text-slate-400 truncate">{p.topic}</p>
          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
            <VideoParam>{p.aspect_ratio || "9:16"}</VideoParam>
            <VideoParam>{p.resolution || "720p"}</VideoParam>
            {p.upscale && <VideoParam>upscaled</VideoParam>}
            {/* what the audio pass actually did, from mix_manifest */}
            {mix?.ducked && <VideoParam>music ducked</VideoParam>}
            {mix?.mastered && <VideoParam>mastered</VideoParam>}
            {p.show_logo && <VideoParam>logo</VideoParam>}
            {p.burn_captions === false && <VideoParam>no burn-in</VideoParam>}
          </div>
          {shots.length > 0 && (
            <div className="flex gap-4 mt-2">
              <div>
                <div className="text-sm font-bold tabular-nums">
                  {shots.length}
                </div>
                <div className="text-[10px] uppercase tracking-wide text-slate-400">
                  Shots
                </div>
              </div>
              {totalSec > 0 && (
                <div>
                  <div className="text-sm font-bold tabular-nums">
                    ~{totalSec}s
                  </div>
                  <div className="text-[10px] uppercase tracking-wide text-slate-400">
                    Length
                  </div>
                </div>
              )}
              {renderedSec > 0 && (
                <div>
                  <div className="text-sm font-bold tabular-nums">
                    {fmtGpuTime(renderedSec)}
                  </div>
                  <div className="text-[10px] uppercase tracking-wide text-slate-400">
                    GPU time
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        {statusChip(p)}
      </div>

      {VIDEO_BUSY_STATUSES.includes(p.status) && (
        <div className="mx-4 mb-2 h-1.5 rounded-full bg-slate-100 overflow-hidden">
          <div
            className="h-full bg-fuchsia-500 rounded-full transition-all"
            style={{ width: `${p.progress_percent || 0}%` }}
          />
        </div>
      )}
      {p.status === "failed" && p.error_message && (
        <p className="mx-4 mb-2 text-xs text-red-600 break-words">
          {p.error_message}
        </p>
      )}

      {/* shot timeline */}
      {shots.length > 0 && (
        <div className="px-4 pb-3">
          <div className="flex items-baseline justify-between mb-1.5">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
              Shots
            </span>
            <span className="text-[10px] text-slate-400 flex items-center gap-3">
              <span className="flex items-center gap-1">
                <i className="w-2.5 h-2.5 rounded-sm bg-slate-900 inline-block" />
                Talking head
              </span>
              <span className="flex items-center gap-1">
                <i className="w-2.5 h-2.5 rounded-sm bg-slate-600 inline-block" />
                B-roll
              </span>
            </span>
          </div>
          <div className="flex gap-1 h-11">
            {shots.map((s, i) => {
              const st = shotStatus(i);
              return (
                <button
                  key={i}
                  onClick={() => setSel(i)}
                  title={s.scene_desc}
                  style={{
                    flex: Math.max(0.6, Number(s.duration_seconds) || 1),
                  }}
                  className={`relative rounded-md overflow-hidden text-left px-2 flex flex-col justify-center min-w-0 transition-shadow ${
                    s.type === "talking_head" ? "bg-slate-900" : "bg-slate-600"
                  } ${sel === i ? "ring-2 ring-fuchsia-500 ring-offset-1" : ""} ${
                    st === "running" ? "animate-pulse" : ""
                  }`}
                >
                  <span className="text-[8px] font-bold uppercase tracking-wide text-white/50">
                    {s.type === "talking_head" ? "talk" : "b-roll"}
                    {s.duration_seconds ? ` · ${s.duration_seconds}s` : ""}
                  </span>
                  <span className="text-[10px] text-white truncate">
                    {s.scene_desc}
                  </span>
                  {(st === "failed" || st === "done") && (
                    <span
                      className={`absolute top-1 right-1 w-2 h-2 rounded-full ${
                        st === "failed" ? "bg-red-500" : "bg-green-400"
                      }`}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* render queue */}
      {jobs.length > 0 && (
        <div className="mx-4 mb-3 border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-3 py-1.5 bg-slate-50 border-b border-slate-100 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
            Render queue
          </div>
          {jobs.map((j) => {
            const [w, cls] = jobBar(j);
            const kindLabel = VIDEO_JOB_KIND_LABELS[j.kind] || j.kind;
            const takeLabel = Number(j.take_index)
              ? ` · take ${Number(j.take_index) + 1}`
              : "";
            const label =
              j.shot_index >= 0
                ? `Shot ${j.shot_index + 1} · ${kindLabel}${takeLabel}`
                : kindLabel;
            return (
              <div
                key={j.id}
                className="flex items-center gap-2.5 px-3 py-1.5 border-b border-slate-50 last:border-b-0"
                title={j.error_message || undefined}
              >
                <span
                  className={`w-2 h-2 rounded-full flex-none ${jobDot(j.status)}`}
                />
                <span className="flex-1 text-xs font-medium text-slate-700 truncate">
                  {label}
                </span>
                <span className="text-[11px] text-slate-400 font-mono tabular-nums">
                  {j.status === "done" && j.gpu_seconds
                    ? `done · ${fmtGpuTime(j.gpu_seconds)}`
                    : ["running", "submitted"].includes(j.status) &&
                        j.step_total
                      ? `sampling ${j.step_current || 0}/${j.step_total}`
                      : j.status}
                </span>
                <div className="w-24 h-1 rounded-full bg-slate-100 overflow-hidden flex-none">
                  <div className={`h-full ${cls}`} style={{ width: w }} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* inspector */}
      {shot && (
        <div className="mx-4 mb-3 border border-slate-200 rounded-lg overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border-b border-slate-100">
            <span className="w-5 h-5 rounded bg-slate-200 text-slate-600 text-[11px] font-bold flex items-center justify-center">
              {sel + 1}
            </span>
            <VideoKindBadge type={shot.type} />
            {shot.type === "talking_head" && (
              <span className="text-[10px] text-slate-400">
                avatar still → animated
              </span>
            )}
          </div>
          <div className="p-3 space-y-2">
            <p className="text-xs font-mono text-slate-600 leading-relaxed">
              {shot.scene_desc}
            </p>
            {shot.dialogue && (
              <p className="text-xs text-slate-500 italic">“{shot.dialogue}”</p>
            )}
            <div className="flex flex-wrap gap-1.5">
              {shot.camera_angle && (
                <VideoParam>
                  <b className="font-semibold text-slate-800">
                    {shot.camera_angle}
                  </b>
                </VideoParam>
              )}
              {shot.camera_move && (
                <VideoParam>
                  <b className="font-semibold text-slate-800">
                    {shot.camera_move}
                  </b>
                </VideoParam>
              )}
              {shot.duration_seconds && (
                <VideoParam>
                  <b className="font-semibold text-slate-800">
                    {shot.duration_seconds}
                  </b>{" "}
                  sec
                </VideoParam>
              )}
              {shot.avatar_ref && <VideoParam>{shot.avatar_ref}</VideoParam>}
              {shot.seed && (
                <VideoParam>
                  seed{" "}
                  <b className="font-semibold text-slate-800">{shot.seed}</b>
                </VideoParam>
              )}
            </div>
            {(() => {
              // keyframe (still/angle) first, then the animated clip
              const artifacts = jobs
                .filter(
                  (j) =>
                    j.shot_index === sel &&
                    j.status === "done" &&
                    (Number(j.take_index) || 0) === latestTake(sel),
                )
                .sort(
                  (a, b) =>
                    VIDEO_IMAGE_KINDS.includes(b.kind) -
                    VIDEO_IMAGE_KINDS.includes(a.kind),
                );
              if (!artifacts.length) return null;
              return (
                <div className="flex items-end gap-3 flex-wrap pt-1">
                  {artifacts.map((j) => (
                    <div key={j.id} className="space-y-1">
                      <VideoShotArtifact
                        projectId={p.id}
                        shotIndex={sel}
                        kind={j.kind}
                      />
                      <p className="text-[9px] uppercase tracking-wide text-slate-400 text-center">
                        {VIDEO_IMAGE_KINDS.includes(j.kind)
                          ? "keyframe"
                          : "animated"}
                      </p>
                    </div>
                  ))}
                </div>
              );
            })()}
            {onTakeSwitched && (
              <VideoTakeStrip
                project={p}
                shotIndex={sel}
                clipKind={
                  shot.type === "talking_head" ? "talking_head" : "broll"
                }
                onSwitched={onTakeSwitched}
              />
            )}
          </div>
          {onRegenerateShot && (
            <div className="flex items-center justify-between gap-3 px-3 py-2 bg-slate-50 border-t border-slate-100">
              <span className="text-[11px] text-slate-400">
                Adds a new take with a fresh seed. Earlier takes are kept, so
                you can compare and pick.
              </span>
              <button
                onClick={() => onRegenerateShot(sel)}
                disabled={regenBusy}
                className="flex items-center gap-1 px-3 py-1.5 bg-fuchsia-600 text-white text-xs font-medium rounded-lg hover:bg-fuchsia-700 disabled:opacity-50 flex-none"
              >
                {regenBusy ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Sparkles className="w-3.5 h-3.5" />
                )}
                New take
              </button>
            </div>
          )}
        </div>
      )}

      {/* foot: existing status-driven actions */}
      <div className="flex items-center gap-2 flex-wrap px-4 py-3 bg-slate-50 border-t border-slate-100">
        {actions}
      </div>
    </div>
  );
};

// Inline player that fetches the finished MP4 with the JWT (plain <video src>
// can't send the auth header).
const VideoProjectPlayer = ({ projectId }) => {
  const [url, setUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => () => url && URL.revokeObjectURL(url), [url]);
  const load = async () => {
    setBusy(true);
    try {
      setUrl(await fetchPodcastAudioUrl(`/video/projects/${projectId}/video`));
    } catch {
      showToast("Could not load the video.");
    } finally {
      setBusy(false);
    }
  };
  if (url)
    return <video controls src={url} className="w-full max-w-xs rounded-lg" />;
  return (
    <button
      onClick={load}
      disabled={busy}
      className="flex items-center gap-1 px-3 py-1.5 bg-fuchsia-600 text-white text-xs font-medium rounded-lg hover:bg-fuchsia-700 disabled:opacity-50"
    >
      <Play className="w-3.5 h-3.5" /> {busy ? "Loading…" : "Watch"}
    </button>
  );
};

// Voiceover track (item 27): replaces text-to-speech, and is what makes a
// b-roll-only project possible (item 26). Editable only between renders,
// matching the endpoint's own gate.
const VideoVoiceoverControl = ({ project, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    const resp = await authUpload(
      `/video/projects/${project.id}/vo-audio`,
      fd,
    );
    setBusy(false);
    const err = podcastErr(resp, "Failed to upload the voiceover.");
    if (err) {
      showToast(err);
      return;
    }
    showToast("Voiceover uploaded — shots will use it instead of text-to-speech.", "success");
    onChanged();
  };
  const clear = async () => {
    setBusy(true);
    const data = await authFetch(`/video/projects/${project.id}/vo-audio`, {
      method: "DELETE",
    });
    setBusy(false);
    const err = podcastErr(data, "Failed to remove the voiceover.");
    if (err) {
      showToast(err);
      return;
    }
    showToast("Voiceover removed — back to text-to-speech.", "success");
    onChanged();
  };

  if (project.vo_audio_path)
    return (
      <div className="flex items-center gap-2">
        <span className="flex items-center gap-1 text-[11px] text-slate-600 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5">
          <Mic className="w-3 h-3" /> Own voiceover · no captions
        </span>
        <button
          onClick={clear}
          disabled={busy}
          className="text-[11px] text-slate-400 hover:text-red-600 disabled:opacity-50"
        >
          Remove
        </button>
      </div>
    );
  return (
    <label
      className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 cursor-pointer"
      title="Use your own narration instead of text-to-speech. Uploaded tracks render without captions."
    >
      {busy ? (
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
      ) : (
        <Mic className="w-3.5 h-3.5" />
      )}
      Voiceover
      <input
        type="file"
        accept=".wav,.mp3,.m4a"
        className="hidden"
        disabled={busy}
        onChange={(e) => upload(e.target.files?.[0])}
      />
    </label>
  );
};

// Take strip for one shot (item 28): every rendered attempt, newest first,
// with the one that's in the video marked. Switching costs no GPU time — the
// takes already exist, so the server only re-cuts.
const VideoTakeStrip = ({ project, shotIndex, clipKind, onSwitched }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    const resp = await authFetch(
      `/video/projects/${project.id}/shots/${shotIndex}/takes`,
    );
    if (!resp.error) setData(resp);
  };
  useEffect(() => {
    setData(null);
    load();
    // eslint-disable-next-line
  }, [project.id, shotIndex, project.updated_at]);

  const select = async (takeIndex) => {
    setBusy(takeIndex);
    const resp = await authFetch(
      `/video/projects/${project.id}/shots/${shotIndex}/take`,
      { method: "PUT", body: JSON.stringify({ take_index: takeIndex }) },
    );
    setBusy(null);
    const err = podcastErr(resp, "Could not switch takes.");
    if (err) {
      showToast(err);
      return;
    }
    showToast("Take selected — re-cutting the video.", "success");
    onSwitched();
  };

  const takes = data?.takes || [];
  // One take is just the original render; a strip would be noise.
  if (takes.length < 2) return null;

  return (
    <div className="pt-2 border-t border-slate-100 space-y-1.5">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
        Takes
      </p>
      <div className="flex gap-2 flex-wrap items-start">
        {takes.map((take) => {
          const selected = data.selected_take === take.take_index;
          return (
            <div
              key={take.take_index}
              className={`rounded-lg border p-1.5 space-y-1 ${
                selected
                  ? "border-fuchsia-400 bg-fuchsia-50"
                  : "border-slate-200"
              }`}
            >
              {take.complete ? (
                <VideoShotArtifact
                  projectId={project.id}
                  shotIndex={shotIndex}
                  kind={clipKind}
                  take={take.take_index}
                />
              ) : (
                <div className="w-28 h-16 rounded bg-slate-100 flex items-center justify-center text-[10px] text-slate-400">
                  {take.kinds.some((k) => k.status === "failed")
                    ? "failed"
                    : "rendering…"}
                </div>
              )}
              <div className="flex items-center justify-between gap-2">
                <span className="text-[10px] text-slate-500">
                  Take {take.take_index + 1}
                  {take.gpu_seconds
                    ? ` · ${fmtGpuTime(take.gpu_seconds)}`
                    : ""}
                </span>
                {selected ? (
                  <span className="text-[10px] font-semibold text-fuchsia-700">
                    In video
                  </span>
                ) : (
                  take.complete && (
                    <button
                      onClick={() => select(take.take_index)}
                      disabled={busy !== null}
                      className="text-[10px] font-medium text-slate-500 hover:text-fuchsia-700 disabled:opacity-50"
                    >
                      {busy === take.take_index ? "…" : "Use"}
                    </button>
                  )
                )}
              </div>
            </div>
          );
        })}
      </div>
      {!data.explicit && (
        <p className="text-[10px] text-slate-400">
          Newest completed take is used unless you pick one.
        </p>
      )}
    </div>
  );
};

// Caption sidecars (item 32): platforms that supply their own captions still
// want the text track, and so does accessibility tooling.
const VideoCaptionDownloads = ({ project }) => {
  const [busy, setBusy] = useState(null);
  const grab = async (fmt) => {
    setBusy(fmt);
    try {
      const url = await fetchPodcastAudioUrl(
        `/video/projects/${project.id}/captions.${fmt}`,
      );
      const name =
        (project.title || `video-${project.id}`)
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-|-$/g, "")
          .slice(0, 60) || `video-${project.id}`;
      const a = document.createElement("a");
      a.href = url;
      a.download = `${name}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      showToast(`No ${fmt.toUpperCase()} captions for this video.`);
    } finally {
      setBusy(null);
    }
  };
  return (
    <span className="flex items-center gap-1">
      {["srt", "vtt"].map((fmt) => (
        <button
          key={fmt}
          onClick={() => grab(fmt)}
          disabled={busy !== null}
          title={`Download ${fmt.toUpperCase()} captions`}
          className="flex items-center gap-1 px-2 py-1.5 bg-slate-100 text-slate-600 text-[11px] font-medium rounded-lg hover:bg-slate-200 disabled:opacity-50"
        >
          {busy === fmt ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <Subtitles className="w-3 h-3" />
          )}
          {fmt.toUpperCase()}
        </button>
      ))}
    </span>
  );
};

// Music bed (item 31): sidechain-ducked under the narration by the same
// podcast mastering pass, so it never fights the speech.
const VideoMusicControl = ({ project, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    const resp = await authUpload(`/video/projects/${project.id}/music`, fd);
    setBusy(false);
    const err = podcastErr(resp, "Failed to upload the music bed.");
    if (err) {
      showToast(err);
      return;
    }
    showToast("Music bed added — it will duck under the narration.", "success");
    onChanged();
  };
  const clear = async () => {
    setBusy(true);
    const data = await authFetch(`/video/projects/${project.id}/music`, {
      method: "DELETE",
    });
    setBusy(false);
    const err = podcastErr(data, "Failed to remove the music bed.");
    if (err) {
      showToast(err);
      return;
    }
    showToast("Music bed removed.", "success");
    onChanged();
  };

  if (project.music_audio_path)
    return (
      <div className="flex items-center gap-2">
        <span
          className="flex items-center gap-1 text-[11px] text-slate-600 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5"
          title="Ducked under the narration via sidechain compression"
        >
          <Music className="w-3 h-3" /> Music bed
          {project.music_gain_db != null
            ? ` · ${Number(project.music_gain_db)} dB`
            : ""}
        </span>
        <button
          onClick={clear}
          disabled={busy}
          className="text-[11px] text-slate-400 hover:text-red-600 disabled:opacity-50"
        >
          Remove
        </button>
      </div>
    );
  return (
    <label
      className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 cursor-pointer"
      title="Add a music bed. It is ducked under the narration automatically."
    >
      {busy ? (
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
      ) : (
        <Music className="w-3.5 h-3.5" />
      )}
      Music
      <input
        type="file"
        accept=".wav,.mp3,.m4a"
        className="hidden"
        disabled={busy}
        onChange={(e) => upload(e.target.files?.[0])}
      />
    </label>
  );
};

// Download the finished MP4. Same authed-blob dance as the player (a plain
// href can't send the JWT), then hand the blob to a synthetic <a download>.
const VideoDownloadButton = ({ project }) => {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const url = await fetchPodcastAudioUrl(
        `/video/projects/${project.id}/video`,
      );
      const name =
        (project.title || `video-${project.id}`)
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-|-$/g, "")
          .slice(0, 60) || `video-${project.id}`;
      const a = document.createElement("a");
      a.href = url;
      a.download = `${name}.mp4`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      showToast("Could not download the video.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      onClick={download}
      disabled={busy}
      title="Download the finished MP4"
      className="flex items-center gap-1 px-3 py-1.5 bg-slate-100 text-slate-700 text-xs font-medium rounded-lg hover:bg-slate-200 disabled:opacity-50"
    >
      {busy ? (
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
      ) : (
        <Download className="w-3.5 h-3.5" />
      )}
      Download
    </button>
  );
};

// Editable shot list at the approval gate — nothing renders without a human
// yes, and now a bad director pass can be fixed instead of only discarded.
// Structural edits (reorder/insert/delete) are safe here because the server
// restricts this endpoint to `shotlist_ready`, i.e. before any video_jobs
// rows exist to be renumbered against shot_index.
const VideoShotListModal = ({
  project,
  avatars,
  onClose,
  onApproved,
  onSaved,
  onPreview,
}) => {
  // Local state is the source of truth once mounted, so a background refresh
  // of the project list can't clobber in-progress edits.
  const [shots, setShots] = useState(() => videoShots(project));
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(null); // 'save' | 'approve'
  const [sel, setSel] = useState(0);

  const hasVoiceover = Boolean(project.vo_audio_path);
  // Which artifact kind is this shot's keyframe (item 29). A front-facing
  // talking head has none — it animates the avatar's own still directly.
  const previewKinds = shots.map((s) =>
    s.type === "broll"
      ? "still"
      : (s.camera_angle || "front") !== "front"
        ? "angle"
        : null,
  );
  // ...and only ask for it once it EXISTS. Rendering the preview on shot type
  // alone fires a 404 on every shot click before the storyboard preview has
  // run — harmless (the component hides itself) but it buries real errors in
  // the console. `jobs` is absent on the project the director returns, which
  // correctly reads as "nothing rendered yet".
  const previewKeyframe = (() => {
    const kind = previewKinds[sel];
    if (!kind) return null;
    const job = (project.jobs || []).find(
      (j) => j.shot_index === sel && j.kind === kind && j.status === "done",
    );
    return job ? { kind } : null;
  })();
  const localError = validateShotsLocal(shots, { hasVoiceover });
  // The avatar this project speaks as — used when a b-roll shot becomes a
  // talking head (the validator requires a non-empty avatar_ref). Falls back
  // to an existing talking head's ref, then to the API's own default.
  const projectAvatar =
    avatars?.find((a) => a.id === project.avatar_id)?.name ||
    shots.find((s) => s.type === "talking_head")?.avatar_ref ||
    "narrator";

  const mutate = (next) => {
    setShots(next);
    setDirty(true);
    if (sel >= next.length) setSel(Math.max(0, next.length - 1));
  };

  const setField = (i, key, value) =>
    mutate(shots.map((s, n) => (n === i ? { ...s, [key]: value } : s)));

  // Switching type has to satisfy the validator's shape rules: b-roll must
  // not carry dialogue, avatar_ref, or audio_ref; a talking head derives its
  // duration from the audio rather than a field.
  const setType = (i, type) =>
    mutate(
      shots.map((s, n) => {
        if (n !== i) return s;
        if (type === s.type) return s;
        if (type === "talking_head")
          return {
            ...s,
            type,
            dialogue: s.dialogue || "",
            avatar_ref: projectAvatar,
            duration_seconds: null,
          };
        const { audio_ref: _dropped, ...rest } = s;
        return {
          ...rest,
          type,
          dialogue: null,
          avatar_ref: null,
          duration_seconds: Number(s.duration_seconds) || 4,
          generation_mode: s.generation_mode || VIDEO_DEFAULT_MODE,
          motion: s.motion || "",
        };
      }),
    );

  const move = (i, delta) => {
    const j = i + delta;
    if (j < 0 || j >= shots.length) return;
    const next = [...shots];
    [next[i], next[j]] = [next[j], next[i]];
    mutate(next);
    setSel(j);
  };

  const remove = (i) => mutate(shots.filter((_, n) => n !== i));

  const add = (type) => {
    if (shots.length >= VIDEO_MAX_SHOTS) {
      showToast(`A shot list can hold at most ${VIDEO_MAX_SHOTS} shots.`);
      return;
    }
    const shot =
      type === "talking_head"
        ? {
            type,
            scene_desc: "",
            dialogue: "",
            avatar_ref: projectAvatar,
            camera_angle: "front",
            camera_move: "static",
            duration_seconds: null,
          }
        : {
            type,
            scene_desc: "",
            dialogue: null,
            avatar_ref: null,
            camera_angle: "wide",
            camera_move: "static",
            duration_seconds: 4,
            generation_mode: VIDEO_DEFAULT_MODE,
            motion: "",
          };
    mutate([...shots, shot]);
    setSel(shots.length);
  };

  // PUT the whole array — the server re-validates through validate_shotlist
  // and returns the cleaned list, which we adopt so local state matches what
  // was actually stored. Returns true on success.
  const save = async () => {
    if (localError) {
      showToast(localError);
      return false;
    }
    setBusy("save");
    const data = await authFetch(`/video/projects/${project.id}/shotlist`, {
      method: "PUT",
      body: JSON.stringify({ shots }),
    });
    setBusy(null);
    const err = podcastErr(data, "Failed to save the shot list.");
    if (err) {
      showToast(err);
      return false;
    }
    const stored = videoShots(data.project || {});
    if (stored.length) setShots(stored);
    setDirty(false);
    onSaved?.();
    return true;
  };

  const approve = async () => {
    if (localError) {
      showToast(localError);
      return;
    }
    if (dirty && !(await save())) return;
    setBusy("approve");
    const data = await authFetch(`/video/projects/${project.id}/approve`, {
      method: "POST",
      body: "{}",
    });
    setBusy(null);
    const err = podcastErr(data, "Failed to queue the render.");
    if (err) {
      showToast(err);
      return;
    }
    showToast("Shot list approved — render queued.", "success");
    onApproved();
  };

  const shot = shots[sel];

  return (
    <PodcastModal title={`Shot list — ${project.title}`} onClose={onClose} wide>
      <div className="space-y-3">
        <p className="text-xs text-slate-500">
          Review and edit the director's plan. Approving starts the GPU render
          — this is the checkpoint before compute is spent.
        </p>

        {/* shot strip: select, reorder, delete */}
        <div className="flex flex-wrap gap-1.5">
          {shots.map((s, i) => (
            <div
              key={i}
              className={`flex items-center rounded-lg border overflow-hidden ${
                sel === i
                  ? "border-fuchsia-500 ring-1 ring-fuchsia-200"
                  : "border-slate-200"
              }`}
            >
              <button
                onClick={() => setSel(i)}
                className={`flex items-center gap-1.5 px-2 py-1.5 text-xs ${
                  sel === i ? "bg-fuchsia-50" : "bg-white hover:bg-slate-50"
                }`}
              >
                <span className="w-4 h-4 rounded bg-slate-200 text-slate-600 text-[10px] font-bold flex items-center justify-center flex-none">
                  {i + 1}
                </span>
                <VideoKindBadge type={s.type} />
                {s.type === "broll" && (
                  <span className="text-[9px] uppercase tracking-wide text-slate-400">
                    {s.generation_mode || VIDEO_DEFAULT_MODE}
                  </span>
                )}
                {!(s.scene_desc || "").trim() && (
                  <span className="text-[10px] text-amber-600 font-medium">
                    empty
                  </span>
                )}
              </button>
              <div className="flex items-center border-l border-slate-200">
                <button
                  onClick={() => move(i, -1)}
                  disabled={i === 0}
                  title="Move earlier"
                  className="px-1 py-1.5 text-slate-400 hover:text-slate-700 disabled:opacity-30"
                >
                  <ChevronUp className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => move(i, 1)}
                  disabled={i === shots.length - 1}
                  title="Move later"
                  className="px-1 py-1.5 text-slate-400 hover:text-slate-700 disabled:opacity-30"
                >
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => remove(i)}
                  title="Delete shot"
                  className="px-1.5 py-1.5 text-slate-400 hover:text-red-600"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => add("talking_head")}
              className="flex items-center gap-1 px-2 py-1.5 text-xs text-slate-600 border border-dashed border-slate-300 rounded-lg hover:border-fuchsia-400 hover:text-fuchsia-700"
            >
              <Plus className="w-3.5 h-3.5" /> Talking head
            </button>
            <button
              onClick={() => add("broll")}
              className="flex items-center gap-1 px-2 py-1.5 text-xs text-slate-600 border border-dashed border-slate-300 rounded-lg hover:border-fuchsia-400 hover:text-fuchsia-700"
            >
              <Plus className="w-3.5 h-3.5" /> B-roll
            </button>
          </div>
        </div>

        {/* editor for the selected shot */}
        {shot && (
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border-b border-slate-100">
              <span className="w-5 h-5 rounded bg-slate-200 text-slate-600 text-[11px] font-bold flex items-center justify-center flex-none">
                {sel + 1}
              </span>
              <select
                value={shot.type}
                onChange={(e) => setType(sel, e.target.value)}
                className="text-xs border border-slate-300 rounded px-2 py-1 bg-white"
              >
                <option value="talking_head">Talking head</option>
                <option value="broll">B-roll</option>
              </select>
              {shot.type === "talking_head" && shot.avatar_ref && (
                <span className="text-xs text-slate-400 font-mono truncate">
                  {shot.avatar_ref}
                </span>
              )}
              {shot.audio_ref && (
                <span
                  className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-600"
                  title="Audio comes from the source podcast episode — editing dialogue will not change the spoken words"
                >
                  episode audio
                </span>
              )}
            </div>
            <div className="p-3 space-y-3">
              <PodcastField label="Scene — what the frame shows" required>
                <textarea
                  className={podcastInputCls}
                  rows={2}
                  maxLength={VIDEO_MAX_SCENE_DESC}
                  value={shot.scene_desc || ""}
                  onChange={(e) => setField(sel, "scene_desc", e.target.value)}
                  placeholder="Subject, setting, mood — not the story"
                />
              </PodcastField>
              {shot.type === "talking_head" ? (
                <PodcastField label="Dialogue — the spoken words" required>
                  <textarea
                    className={podcastInputCls}
                    rows={4}
                    maxLength={VIDEO_MAX_DIALOGUE}
                    value={shot.dialogue || ""}
                    onChange={(e) => setField(sel, "dialogue", e.target.value)}
                  />
                </PodcastField>
              ) : (
                <>
                  <PodcastField label="How this shot is generated">
                    <select
                      className={podcastInputCls}
                      value={shot.generation_mode || VIDEO_DEFAULT_MODE}
                      onChange={(e) =>
                        setField(sel, "generation_mode", e.target.value)
                      }
                    >
                      {VIDEO_GENERATION_MODES.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.label}
                        </option>
                      ))}
                    </select>
                    <p className="text-[11px] text-slate-400 mt-1">
                      {
                        VIDEO_GENERATION_MODES.find(
                          (m) =>
                            m.id === (shot.generation_mode || VIDEO_DEFAULT_MODE),
                        )?.hint
                      }
                    </p>
                  </PodcastField>
                  {(shot.generation_mode || VIDEO_DEFAULT_MODE) !== "camera" && (
                    <PodcastField label="What moves in the shot" required>
                      <textarea
                        className={podcastInputCls}
                        rows={2}
                        maxLength={VIDEO_MAX_MOTION}
                        value={shot.motion || ""}
                        onChange={(e) => setField(sel, "motion", e.target.value)}
                        placeholder="e.g. a hiker and his dog walk along the path, the camera following them around the corner"
                      />
                    </PodcastField>
                  )}
                  <PodcastField label="Length (seconds)" required>
                    <input
                      type="number"
                      min={VIDEO_BROLL_DURATION[0]}
                      max={VIDEO_BROLL_DURATION[1]}
                      step="0.5"
                      className={podcastInputCls}
                      value={shot.duration_seconds ?? ""}
                      onChange={(e) =>
                        setField(
                          sel,
                          "duration_seconds",
                          e.target.value === "" ? "" : Number(e.target.value),
                        )
                      }
                    />
                    <p className="text-[11px] text-slate-400 mt-1">
                      Max {VIDEO_BROLL_DURATION[1]}s — one generation. Longer
                      shots need to be split.
                    </p>
                  </PodcastField>
                </>
              )}
              <div className="grid grid-cols-2 gap-3">
                <PodcastField label="Camera angle">
                  <select
                    className={podcastInputCls}
                    value={shot.camera_angle || "front"}
                    onChange={(e) =>
                      setField(sel, "camera_angle", e.target.value)
                    }
                  >
                    {VIDEO_ANGLES.map((a) => (
                      <option key={a} value={a}>
                        {a.replace(/_/g, " ")}
                      </option>
                    ))}
                  </select>
                </PodcastField>
                <PodcastField
                  label={
                    shot.type === "broll" &&
                    (shot.generation_mode || VIDEO_DEFAULT_MODE) !== "camera"
                      ? "Camera move (used in camera-only mode)"
                      : "Camera move"
                  }
                >
                  <select
                    className={podcastInputCls}
                    value={shot.camera_move || "static"}
                    onChange={(e) =>
                      setField(sel, "camera_move", e.target.value)
                    }
                  >
                    {VIDEO_MOVES.map((m) => (
                      <option key={m} value={m}>
                        {m.replace(/_/g, " ")}
                      </option>
                    ))}
                  </select>
                </PodcastField>
              </div>
            </div>
          </div>
        )}

        {onPreview && (
          <div className="flex items-start justify-between gap-3 p-2 rounded-lg bg-slate-50 border border-slate-200">
            <span className="text-[11px] text-slate-500">
              Render just the keyframes first to see how the shots look. Costs
              a fraction of a full render, and the frames are reused when you
              approve.
            </span>
            <button
              onClick={onPreview}
              disabled={Boolean(busy) || Boolean(localError)}
              className="flex items-center gap-1 px-3 py-1.5 bg-white border border-slate-300 text-slate-700 text-xs font-medium rounded-lg hover:border-fuchsia-400 hover:text-fuchsia-700 disabled:opacity-50 flex-none"
            >
              <Image className="w-3.5 h-3.5" /> Preview keyframes
            </button>
          </div>
        )}
        {shot && previewKeyframe && (
          <div className="flex items-end gap-3 flex-wrap">
            <div className="space-y-1">
              <VideoShotArtifact
                projectId={project.id}
                shotIndex={sel}
                kind={previewKeyframe.kind}
              />
              <p className="text-[9px] uppercase tracking-wide text-slate-400 text-center">
                keyframe · shot {sel + 1}
              </p>
            </div>
          </div>
        )}
        {hasVoiceover && (
          <p className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-2">
            This project uses your uploaded voiceover, so shots are not
            narrated by text-to-speech.{" "}
            <b className="font-semibold">Captions are not burned in</b> — an
            uploaded track carries no word timings.
          </p>
        )}
        {localError && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
            {localError}
          </p>
        )}

        <div className="flex items-center justify-between gap-2 pt-1">
          <span className="text-[11px] text-slate-400">
            {shots.length} shot{shots.length === 1 ? "" : "s"}
            {dirty ? " · unsaved changes" : ""}
          </span>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg"
            >
              Close
            </button>
            <button
              onClick={save}
              disabled={!dirty || Boolean(busy) || Boolean(localError)}
              className="flex items-center gap-1.5 px-4 py-2 bg-slate-100 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-200 disabled:opacity-50"
            >
              {busy === "save" && <Loader2 className="w-4 h-4 animate-spin" />}
              Save changes
            </button>
            <button
              onClick={approve}
              disabled={Boolean(busy) || Boolean(localError)}
              title={dirty ? "Saves your edits, then queues the render" : undefined}
              className="flex items-center gap-1.5 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {busy === "approve" && (
                <Loader2 className="w-4 h-4 animate-spin" />
              )}
              {dirty ? "Save & Approve" : "Approve & Render"}
            </button>
          </div>
        </div>
      </div>
    </PodcastModal>
  );
};

const VideoAvatarForm = ({ facilityId, initial, onClose, onSaved }) => {
  // Voices come from the podcast registry (Rule 1 — one voice registry), via
  // the endpoint the Podcast portal already uses. Video synthesis is Azure-
  // only, so non-Azure rows are filtered out rather than offered and rejected.
  const [voices, setVoices] = useState([]);
  useEffect(() => {
    authFetch(`/podcast/voices?facility_id=${facilityId}`).then((data) => {
      if (!data.error)
        setVoices(
          (data.voices || []).filter(
            (v) => v.provider === "azure" && v.is_active,
          ),
        );
    });
  }, [facilityId]);

  const [form, setForm] = useState({
    name: initial?.name || "",
    subject_desc: initial?.subject_desc || "",
    aesthetic: initial?.aesthetic || "oil_portrait",
    voice_id: initial?.voice_id || "",
    is_real_person: initial?.is_real_person || false,
    consent_doc: initial?.consent_doc || "",
    consent_signer: initial?.consent_signer || "",
    consent_date: initial?.consent_date
      ? String(initial.consent_date).slice(0, 10)
      : "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!form.name.trim() || !form.subject_desc.trim()) {
      showToast("Name and subject description are required.");
      return;
    }
    setSaving(true);
    const body = JSON.stringify({
      ...form,
      voice_id: form.voice_id === "" ? null : Number(form.voice_id),
      facility_id: facilityId,
    });
    const data = initial?.id
      ? await authFetch(`/video/avatars/${initial.id}`, {
          method: "PUT",
          body,
        })
      : await authFetch(`/video/avatars`, { method: "POST", body });
    setSaving(false);
    const err = podcastErr(data, "Failed to save avatar.");
    if (err) {
      showToast(err);
      return;
    }
    showToast(initial?.id ? "Avatar updated." : "Avatar created.", "success");
    onSaved();
  };

  return (
    <PodcastModal
      title={initial?.id ? "Edit Avatar" : "New Avatar"}
      onClose={onClose}
    >
      <div className="space-y-4">
        <div className="flex items-start gap-2 text-xs text-red-700 bg-red-50 rounded-lg p-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          Real political candidates and living public officials are never
          generated — there is no consent path and no override. Use deceased
          historical figures or fictional subjects. A consenting private person
          (e.g. your own likeness) requires the full consent record below.
        </div>
        <PodcastField label="Name" required>
          <input
            className={podcastInputCls}
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            placeholder="e.g. Hamilton"
          />
        </PodcastField>
        <PodcastField label="Subject description" required>
          <textarea
            className={podcastInputCls}
            rows={3}
            value={form.subject_desc}
            onChange={(e) => set("subject_desc", e.target.value)}
            placeholder="Who the avatar depicts and how they look on camera"
          />
        </PodcastField>
        <PodcastField label="Aesthetic">
          <select
            className={podcastInputCls}
            value={form.aesthetic}
            onChange={(e) => set("aesthetic", e.target.value)}
          >
            {VIDEO_AESTHETICS.map((a) => (
              <option key={a} value={a}>
                {a.replace("_", " ")}
              </option>
            ))}
          </select>
        </PodcastField>
        <PodcastField label="Voice">
          <select
            className={podcastInputCls}
            value={form.voice_id}
            onChange={(e) => set("voice_id", e.target.value)}
          >
            <option value="">Default narration voice</option>
            {voices.map((v) => (
              <option key={v.id} value={v.id}>
                {v.display_name}
                {v.default_style ? ` · ${v.default_style}` : ""}
                {v.is_cloned ? " · cloned (consent on file)" : ""}
              </option>
            ))}
          </select>
          <p className="text-[11px] text-slate-400 mt-1">
            Voices are shared with Podcast Studio. Azure voices only for now —
            video captions need word timings the other providers don't return.
          </p>
        </PodcastField>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={form.is_real_person}
            onChange={(e) => set("is_real_person", e.target.checked)}
            className="rounded"
          />
          This depicts a real (private) person
        </label>
        {form.is_real_person && (
          <>
            <PodcastField label="Consent document reference" required>
              <input
                className={podcastInputCls}
                value={form.consent_doc}
                onChange={(e) => set("consent_doc", e.target.value)}
                placeholder="Link or file reference to signed consent"
              />
            </PodcastField>
            <div className="grid grid-cols-2 gap-3">
              <PodcastField label="Consent signed by" required>
                <input
                  className={podcastInputCls}
                  value={form.consent_signer}
                  onChange={(e) => set("consent_signer", e.target.value)}
                />
              </PodcastField>
              <PodcastField label="Consent date" required>
                <input
                  type="date"
                  className={podcastInputCls}
                  value={form.consent_date}
                  onChange={(e) => set("consent_date", e.target.value)}
                />
              </PodcastField>
            </div>
            {initial?.id && (
              <label className="text-xs text-slate-500 underline cursor-pointer">
                {initial?.consent_file_path
                  ? "Replace signed document"
                  : "Upload signed document (pdf/jpg/png)"}
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const fd = new FormData();
                    fd.append("file", file);
                    const resp = await authUpload(
                      `/video/avatars/${initial.id}/consent-doc`,
                      fd,
                    );
                    if (resp.error) {
                      showToast("Failed to upload consent document.");
                    } else {
                      showToast("Consent document stored.", "success");
                      onSaved();
                    }
                  }}
                />
              </label>
            )}
          </>
        )}
        {initial?.id && (
          <PodcastField label="Master still (png/jpg — the talking-head face)">
            <input
              type="file"
              accept=".png,.jpg,.jpeg"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                const fd = new FormData();
                fd.append("file", file);
                const resp = await authUpload(
                  `/video/avatars/${initial.id}/still`,
                  fd,
                );
                if (resp.error) {
                  showToast("Failed to upload still.");
                } else {
                  showToast("Master still uploaded.", "success");
                  onSaved();
                }
              }}
              className="text-xs text-slate-600 file:mr-2 file:py-1 file:px-3 file:rounded file:border-0 file:text-xs file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
            />
            {initial?.master_still_path && (
              <p className="text-xs text-green-700 mt-1">Still on file.</p>
            )}
          </PodcastField>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="flex items-center gap-1.5 px-4 py-2 bg-fuchsia-600 text-white text-sm font-medium rounded-lg hover:bg-fuchsia-700 disabled:opacity-50"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Save Avatar
          </button>
        </div>
      </div>
    </PodcastModal>
  );
};

export const VideoPortal = ({ facility, user }) => {
  const fid = facility.facility_id;
  const isAdmin =
    facility.is_facility_admin ||
    facility.services?.find((s) => s.service_type === "video")?.access_role ===
      "admin";

  const [tab, setTab] = useState("projects");
  const [projects, setProjects] = useState([]);
  const [avatars, setAvatars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [projectForm, setProjectForm] = useState(null);
  const [avatarForm, setAvatarForm] = useState(null);
  const [shotListView, setShotListView] = useState(null);
  const [actionBusy, setActionBusy] = useState(null);
  const [directing, setDirecting] = useState(null);
  const [costs, setCosts] = useState(null);
  const [costsLoading, setCostsLoading] = useState(false);
  const [shareData, setShareData] = useState(null); // {project, url}

  const openShare = async (p) => {
    setActionBusy(`share-${p.id}`);
    const data = await authFetch(`/video/projects/${p.id}/share-link`, {
      method: "POST",
    });
    setActionBusy(null);
    if (data.error || !data.url) {
      showToast(data.error || "Could not create a share link.", "error");
      return;
    }
    setShareData({ project: p, url: data.url });
  };

  const loadCosts = async () => {
    setCostsLoading(true);
    const data = await authFetch(`/video/costs?facility_id=${fid}`);
    setCostsLoading(false);
    if (!data.error) setCosts(data);
  };

  useEffect(() => {
    if (tab === "usage" && !costs && !costsLoading) loadCosts();
    // eslint-disable-next-line
  }, [tab]);

  const refreshProjects = async () => {
    const data = await authFetch(`/video/projects?facility_id=${fid}`);
    if (!data.error) setProjects(data.projects || []);
  };
  const refreshAvatars = async () => {
    const data = await authFetch(`/video/avatars?facility_id=${fid}`);
    if (!data.error) setAvatars(data.avatars || []);
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([refreshProjects(), refreshAvatars()]);
      setLoading(false);
    })();
    // eslint-disable-next-line
  }, [fid]);

  // Poll while anything renders
  useEffect(() => {
    if (!projects.some((p) => VIDEO_BUSY_STATUSES.includes(p.status)))
      return undefined;
    const t = setInterval(refreshProjects, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [projects]);

  const runAction = async (key, endpoint, okMsg) => {
    setActionBusy(key);
    const data = await authFetch(endpoint, { method: "POST", body: "{}" });
    setActionBusy(null);
    const err = podcastErr(data, "Action failed. Please try again.");
    if (err) {
      showToast(err);
      return;
    }
    if (okMsg) showToast(okMsg, "success");
    refreshProjects();
  };

  const direct = async (project) => {
    setDirecting(project.id);
    const data = await authFetch(`/video/projects/${project.id}/direct`, {
      method: "POST",
      body: "{}",
    });
    setDirecting(null);
    const err = podcastErr(data, "The director could not build a shot list.");
    if (err) {
      showToast(err);
      return;
    }
    await refreshProjects();
    setShotListView(data.project);
  };

  const saveProject = async () => {
    if (!projectForm.title?.trim() || !projectForm.topic?.trim()) {
      showToast("Title and topic are required.");
      return;
    }
    const data = await authFetch(`/video/projects`, {
      method: "POST",
      body: JSON.stringify({ ...projectForm, facility_id: fid }),
    });
    const err = podcastErr(data, "Failed to create project.");
    if (err) {
      showToast(err);
      return;
    }
    setProjectForm(null);
    await refreshProjects();
    direct(data.project);
  };

  const archiveProject = async (p) => {
    if (!window.confirm(`Archive "${p.title}"?`)) return;
    const data = await authFetch(`/video/projects/${p.id}`, {
      method: "DELETE",
    });
    const err = podcastErr(data, "Failed to archive project.");
    if (err) {
      showToast(err);
      return;
    }
    refreshProjects();
  };

  const statusChip = (p) => {
    if (VIDEO_BUSY_STATUSES.includes(p.status)) {
      const hint =
        p.status === "previewing"
          ? "Rendering keyframes…"
          : p.status === "queued" && p.gpu_pod_state === "starting"
            ? "Starting GPU…"
            : p.status;
      return (
        <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">
          {hint}
        </span>
      );
    }
    const styles = {
      draft: "bg-slate-100 text-slate-600",
      shotlist_ready: "bg-blue-100 text-blue-700",
      ready: "bg-green-100 text-green-700",
      published: "bg-fuchsia-100 text-fuchsia-700",
      failed: "bg-red-100 text-red-700",
    };
    return (
      <span
        className={`text-xs px-2 py-0.5 rounded-full font-medium ${
          styles[p.status] || "bg-slate-100 text-slate-600"
        }`}
      >
        {p.status === "shotlist_ready" ? "awaiting approval" : p.status}
      </span>
    );
  };

  if (loading)
    return (
      <div className="p-6 text-center text-slate-400">
        Loading Video Studio…
      </div>
    );

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex space-x-1 mb-6 border-b border-slate-200">
        {[
          { id: "projects", label: "Projects" },
          ...(isAdmin
            ? [
                { id: "avatars", label: "Avatars" },
                { id: "usage", label: "Usage" },
              ]
            : []),
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t.id
                ? "border-fuchsia-500 text-fuchsia-700"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "projects" && (
        <div className="space-y-3">
          {isAdmin && (
            <div className="flex justify-end">
              <button
                onClick={() => {
                  if (!costs && !costsLoading) loadCosts();
                  setProjectForm({
                    title: "",
                    topic: "",
                    source_text: "",
                    avatar_id: avatars[0]?.id || null,
                    aesthetic: "oil_portrait",
                    upscale: false,
                    aspect_ratio: "9:16",
                    resolution: "720p",
                    target_seconds: 75,
                    show_logo: true,
                    logo_position: "bottom_right",
                    brand_captions: true,
                    burn_captions: true,
                  });
                }}
                className="flex items-center gap-1.5 px-4 py-2 bg-fuchsia-600 text-white text-sm font-medium rounded-lg hover:bg-fuchsia-700"
              >
                <Plus className="w-4 h-4" /> New Video
              </button>
            </div>
          )}
          {projects.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200 p-12 text-center text-sm text-slate-400">
              No videos yet.
            </div>
          ) : (
            projects.map((p) => (
              <VideoProductionCard
                key={p.id}
                project={p}
                statusChip={statusChip}
                onRegenerateShot={
                  isAdmin && ["ready", "published", "failed"].includes(p.status)
                    ? (idx) =>
                        runAction(
                          `regen-shot-${p.id}-${idx}`,
                          `/video/projects/${p.id}/shots/${idx}/regenerate`,
                          "New take queued — the earlier take is kept.",
                        )
                    : null
                }
                regenBusy={Boolean(
                  actionBusy && actionBusy.startsWith(`regen-shot-${p.id}-`),
                )}
                onTakeSwitched={
                  isAdmin && ["ready", "published"].includes(p.status)
                    ? refreshProjects
                    : null
                }
                actions={
                  <>
                    {isAdmin && p.status === "draft" && (
                      <button
                        onClick={() => direct(p)}
                        disabled={directing === p.id}
                        className="flex items-center gap-1 px-3 py-1.5 bg-fuchsia-600 text-white text-xs font-medium rounded-lg hover:bg-fuchsia-700 disabled:opacity-50"
                      >
                        {directing === p.id ? (
                          <>
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />{" "}
                            Directing…
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-3.5 h-3.5" /> Build shot list
                          </>
                        )}
                      </button>
                    )}
                    {isAdmin &&
                      ["draft", "shotlist_ready", "failed"].includes(
                        p.status,
                      ) && (
                        <VideoVoiceoverControl
                          project={p}
                          onChanged={refreshProjects}
                        />
                      )}
                    {isAdmin &&
                      !VIDEO_BUSY_STATUSES.includes(p.status) && (
                        <VideoMusicControl
                          project={p}
                          onChanged={refreshProjects}
                        />
                      )}
                    {isAdmin && p.status === "shotlist_ready" && (
                      <button
                        onClick={() => setShotListView(p)}
                        className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700"
                      >
                        Review & approve
                      </button>
                    )}
                    {isAdmin && p.status === "failed" && (
                      <button
                        onClick={() =>
                          runAction(
                            `retry-${p.id}`,
                            `/video/projects/${p.id}/retry`,
                            "Render re-queued — completed shots are reused.",
                          )
                        }
                        disabled={actionBusy === `retry-${p.id}`}
                        className="px-3 py-1.5 bg-slate-100 text-slate-700 text-xs font-medium rounded-lg hover:bg-slate-200 disabled:opacity-50"
                      >
                        Retry
                      </button>
                    )}
                    {["ready", "published"].includes(p.status) && (
                      <>
                        <VideoProjectPlayer projectId={p.id} />
                        <VideoDownloadButton project={p} />
                        <VideoCaptionDownloads project={p} />
                      </>
                    )}
                    {isAdmin && ["ready", "published"].includes(p.status) && (
                      <button
                        onClick={() => openShare(p)}
                        disabled={actionBusy === `share-${p.id}`}
                        title="Publish this video to connected social accounts"
                        className="flex items-center gap-1 px-3 py-1.5 bg-sky-600 text-white text-xs font-medium rounded-lg hover:bg-sky-700 disabled:opacity-50"
                      >
                        {actionBusy === `share-${p.id}` ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Share2 className="w-3.5 h-3.5" />
                        )}
                        Share
                      </button>
                    )}
                    {isAdmin && p.status === "ready" && (
                      <button
                        onClick={() =>
                          runAction(
                            `publish-${p.id}`,
                            `/video/projects/${p.id}/publish`,
                            "Video published.",
                          )
                        }
                        disabled={actionBusy === `publish-${p.id}`}
                        className="px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 disabled:opacity-50"
                      >
                        Publish
                      </button>
                    )}
                    {isAdmin && !VIDEO_BUSY_STATUSES.includes(p.status) && (
                      <button
                        onClick={() => archiveProject(p)}
                        className="px-3 py-1.5 text-xs text-red-500 hover:text-red-700"
                      >
                        Archive
                      </button>
                    )}
                  </>
                }
              />
            ))
          )}
        </div>
      )}

      {tab === "usage" && isAdmin && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-xs text-slate-400">
              GPU rendering usage per month. Budgets reset on the 1st.
            </p>
            <button
              onClick={loadCosts}
              disabled={costsLoading}
              className="text-xs px-3 py-1.5 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 disabled:opacity-50"
            >
              Refresh
            </button>
          </div>
          {costsLoading || !costs ? (
            <div className="text-center py-12 text-sm text-slate-400">
              Loading usage…
            </div>
          ) : (
            <>
              {costs.quota?.exceeded && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                  {costs.quota.exceeded}. Rendering is paused until the budget
                  resets or is raised.
                </div>
              )}
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <p className="text-xs text-slate-500 mb-1">
                  GPU minutes this month
                </p>
                <p className="text-xl font-bold text-slate-900">
                  {Math.round((costs.quota?.gpu_seconds_used || 0) / 60)}
                  <span className="text-sm font-normal text-slate-400">
                    {costs.quota?.gpu_seconds_budget
                      ? ` / ${Math.round(costs.quota.gpu_seconds_budget / 60)}`
                      : ""}{" "}
                    min
                    {costs.gpu_hourly_rate
                      ? ` · est. $${(((costs.quota?.gpu_seconds_used || 0) / 3600) * costs.gpu_hourly_rate).toFixed(2)}`
                      : ""}
                  </span>
                </p>
                {costs.quota?.gpu_seconds_budget ? (
                  <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden">
                    <div
                      className={`h-full rounded-full ${
                        costs.quota.gpu_seconds_used >=
                        costs.quota.gpu_seconds_budget
                          ? "bg-red-500"
                          : costs.quota.gpu_seconds_used >=
                              costs.quota.gpu_seconds_budget * 0.8
                            ? "bg-amber-400"
                            : "bg-fuchsia-500"
                      }`}
                      style={{
                        width: `${Math.min(
                          100,
                          (costs.quota.gpu_seconds_used /
                            costs.quota.gpu_seconds_budget) *
                            100,
                        )}%`,
                      }}
                    />
                  </div>
                ) : (
                  <p className="mt-2 text-xs text-slate-400">
                    No monthly budget set — unlimited.
                  </p>
                )}
              </div>
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 text-left text-xs text-slate-500">
                      <th className="px-4 py-2 font-medium">Month</th>
                      <th className="px-4 py-2 font-medium text-right">
                        Projects
                      </th>
                      <th className="px-4 py-2 font-medium text-right">
                        Jobs done
                      </th>
                      <th className="px-4 py-2 font-medium text-right">
                        GPU minutes
                      </th>
                      <th className="px-4 py-2 font-medium text-right">
                        Est. cost
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(costs.months || []).length === 0 ? (
                      <tr>
                        <td
                          colSpan={5}
                          className="px-4 py-8 text-center text-slate-400"
                        >
                          No usage recorded yet.
                        </td>
                      </tr>
                    ) : (
                      costs.months.map((m, i) => (
                        <tr
                          key={i}
                          className="border-t border-slate-100 text-slate-700"
                        >
                          <td className="px-4 py-2">{m.month?.slice(0, 7)}</td>
                          <td className="px-4 py-2 text-right">{m.projects}</td>
                          <td className="px-4 py-2 text-right">
                            {m.jobs_done}
                          </td>
                          <td className="px-4 py-2 text-right">
                            {Math.round((m.gpu_seconds || 0) / 60)}
                          </td>
                          <td className="px-4 py-2 text-right">
                            {m.estimated_dollars != null
                              ? `$${m.estimated_dollars.toFixed(2)}`
                              : "—"}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {tab === "avatars" && isAdmin && (
        <div className="space-y-3">
          <div className="flex justify-end">
            <button
              onClick={() => setAvatarForm({})}
              className="flex items-center gap-1.5 px-4 py-2 bg-fuchsia-600 text-white text-sm font-medium rounded-lg hover:bg-fuchsia-700"
            >
              <Plus className="w-4 h-4" /> New Avatar
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {avatars.map((a) => (
              <div
                key={a.id}
                className="bg-white rounded-xl border border-slate-200 p-4 space-y-1"
              >
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-slate-900">
                    {a.name}
                  </h4>
                  <button
                    onClick={() => setAvatarForm(a)}
                    className="text-slate-400 hover:text-fuchsia-600"
                    aria-label="Edit avatar"
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-xs text-slate-500 line-clamp-2">
                  {a.subject_desc}
                </p>
                <div className="flex items-center gap-1.5 flex-wrap pt-1">
                  {a.aesthetic && (
                    <span className="px-1.5 py-0.5 rounded-full text-xs bg-slate-100 text-slate-600">
                      {a.aesthetic}
                    </span>
                  )}
                  {a.voice_id && (
                    <span className="px-1.5 py-0.5 rounded-full text-xs bg-slate-100 text-slate-600">
                      custom voice
                    </span>
                  )}
                  {a.master_still_path ? (
                    <span className="px-1.5 py-0.5 rounded-full text-xs bg-green-100 text-green-700">
                      Still set
                    </span>
                  ) : (
                    <span className="px-1.5 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700">
                      Needs still
                    </span>
                  )}
                  {a.is_real_person && (
                    <span className="px-1.5 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700">
                      Real person · consent on file
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {projectForm !== null && (
        <PodcastModal
          title="New Video"
          onClose={() => setProjectForm(null)}
          wide
        >
          <div className="grid md:grid-cols-[1.2fr_0.8fr] gap-5">
            <div className="space-y-4">
              <PodcastField label="Title" required>
                <input
                  className={podcastInputCls}
                  value={projectForm.title}
                  onChange={(e) =>
                    setProjectForm((f) => ({ ...f, title: e.target.value }))
                  }
                />
              </PodcastField>
              <PodcastField label="Topic" required>
                <input
                  className={podcastInputCls}
                  value={projectForm.topic}
                  onChange={(e) =>
                    setProjectForm((f) => ({ ...f, topic: e.target.value }))
                  }
                  placeholder="What the video is about"
                />
              </PodcastField>
              <PodcastField label="Source text">
                <textarea
                  className={podcastInputCls}
                  rows={5}
                  value={projectForm.source_text}
                  onChange={(e) =>
                    setProjectForm((f) => ({
                      ...f,
                      source_text: e.target.value,
                    }))
                  }
                  placeholder="Optional — the material the speaker draws dialogue from. Leave blank to work from the topic alone."
                />
              </PodcastField>
              <div className="grid grid-cols-2 gap-3">
                <PodcastField label="Avatar" required>
                  <select
                    className={podcastInputCls}
                    value={projectForm.avatar_id || ""}
                    onChange={(e) =>
                      setProjectForm((f) => ({
                        ...f,
                        avatar_id: Number(e.target.value) || null,
                      }))
                    }
                  >
                    <option value="">— Pick an avatar —</option>
                    {avatars.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                      </option>
                    ))}
                  </select>
                </PodcastField>
                <PodcastField label="Aesthetic">
                  <select
                    className={podcastInputCls}
                    value={projectForm.aesthetic}
                    onChange={(e) =>
                      setProjectForm((f) => ({
                        ...f,
                        aesthetic: e.target.value,
                      }))
                    }
                  >
                    {VIDEO_AESTHETICS.map((a) => (
                      <option key={a} value={a}>
                        {a.replace("_", " ")}
                      </option>
                    ))}
                  </select>
                </PodcastField>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <PodcastField label="Format">
                  <select
                    className={podcastInputCls}
                    value={projectForm.aspect_ratio}
                    onChange={(e) =>
                      setProjectForm((f) => ({
                        ...f,
                        aspect_ratio: e.target.value,
                      }))
                    }
                  >
                    {VIDEO_ASPECTS.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.label} — {a.hint}
                      </option>
                    ))}
                  </select>
                </PodcastField>
                <PodcastField label="Target length">
                  <select
                    className={podcastInputCls}
                    value={projectForm.target_seconds}
                    onChange={(e) =>
                      setProjectForm((f) => ({
                        ...f,
                        target_seconds: Number(e.target.value),
                      }))
                    }
                  >
                    {VIDEO_TARGET_SECONDS.map((s) => (
                      <option key={s} value={s}>
                        {s < 60
                          ? `${s} seconds`
                          : `${s / 60} minute${s === 60 ? "" : "s"}`}
                      </option>
                    ))}
                  </select>
                </PodcastField>
              </div>
              <PodcastField label="Resolution">
                <div className="flex gap-2">
                  {VIDEO_RESOLUTIONS.map((r) => (
                    <button
                      key={r}
                      type="button"
                      onClick={() =>
                        setProjectForm((f) => ({ ...f, resolution: r }))
                      }
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg border ${
                        projectForm.resolution === r
                          ? "bg-fuchsia-50 border-fuchsia-400 text-fuchsia-700"
                          : "bg-white border-slate-300 text-slate-600 hover:border-slate-400"
                      }`}
                    >
                      {r}
                    </button>
                  ))}
                </div>
                {projectForm.resolution === "1080p" &&
                  !projectForm.upscale && (
                    <p className="text-[11px] text-amber-700 mt-1">
                      Shots render at the model's native size and are scaled to
                      1080p on assembly. For a true 1080p master, also turn on
                      the upscale pass below.
                    </p>
                  )}
              </PodcastField>
              <div className="space-y-2 pt-1 border-t border-slate-100">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                  Branding
                </p>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={Boolean(projectForm.show_logo)}
                    onChange={(e) =>
                      setProjectForm((f) => ({
                        ...f,
                        show_logo: e.target.checked,
                      }))
                    }
                    className="rounded"
                  />
                  Show the facility logo
                </label>
                {projectForm.show_logo && (
                  <select
                    className={podcastInputCls}
                    value={projectForm.logo_position}
                    onChange={(e) =>
                      setProjectForm((f) => ({
                        ...f,
                        logo_position: e.target.value,
                      }))
                    }
                  >
                    {VIDEO_LOGO_POSITIONS.map((pos) => (
                      <option key={pos} value={pos}>
                        {pos.replace(/_/g, " ")}
                      </option>
                    ))}
                  </select>
                )}
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={Boolean(projectForm.brand_captions)}
                    onChange={(e) =>
                      setProjectForm((f) => ({
                        ...f,
                        brand_captions: e.target.checked,
                      }))
                    }
                    className="rounded"
                  />
                  Colour captions with the facility brand colour
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={Boolean(projectForm.burn_captions)}
                    onChange={(e) =>
                      setProjectForm((f) => ({
                        ...f,
                        burn_captions: e.target.checked,
                      }))
                    }
                    className="rounded"
                  />
                  Burn captions into the picture
                </label>
                <p className="text-[11px] text-slate-400">
                  The logo and brand colour come from the facility's shared
                  settings. Subtitle files (.srt/.vtt) are exported either way.
                </p>
              </div>
              <label className="flex items-start gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={Boolean(projectForm.upscale)}
                  onChange={(e) =>
                    setProjectForm((f) => ({ ...f, upscale: e.target.checked }))
                  }
                  className="rounded mt-0.5"
                />
                <span>
                  Upscale to 1080p after assembly
                  <span className="block text-xs text-slate-400">
                    A second GPU pass over the finished video — sharper output,
                    extra render time and cost. The original stays on file.
                  </span>
                </span>
              </label>
            </div>

            {/* estimate pane (video-create-flow.html right pane) — real
                quota/usage numbers only, no fabricated VRAM figures */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-4 self-start">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">
                  What happens next
                </p>
                <ol className="space-y-1.5 text-xs text-slate-600 leading-relaxed">
                  <li>
                    <b className="text-slate-800">1.</b> The director turns your
                    source text into a shot list
                  </li>
                  <li>
                    <b className="text-slate-800">2.</b> You review and approve
                    it — no GPU time is spent before that
                  </li>
                  <li>
                    <b className="text-slate-800">3.</b> Each shot renders on
                    the GPU, then the video is assembled
                  </li>
                </ol>
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">
                  GPU
                </p>
                <div className="bg-white border border-slate-200 rounded-lg p-3 space-y-2 text-xs">
                  {(() => {
                    const used = Math.round(
                      (costs?.quota?.gpu_seconds_used || 0) / 60,
                    );
                    const budget = costs?.quota?.gpu_seconds_budget
                      ? Math.round(costs.quota.gpu_seconds_budget / 60)
                      : null;
                    const months = costs?.months || [];
                    const histGpu = months.reduce(
                      (a, m) => a + (Number(m.gpu_seconds) || 0),
                      0,
                    );
                    const histProjects = months.reduce(
                      (a, m) => a + (Number(m.projects) || 0),
                      0,
                    );
                    const avgMin =
                      histProjects > 0
                        ? Math.max(1, Math.round(histGpu / histProjects / 60))
                        : null;
                    if (!costs)
                      return <p className="text-slate-400">Loading usage…</p>;
                    return (
                      <>
                        <div className="flex justify-between">
                          <span className="text-slate-500">This month</span>
                          <b className="tabular-nums">
                            {used}
                            {budget ? ` / ${budget}` : ""} min
                          </b>
                        </div>
                        {budget && (
                          <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                            <div
                              className="h-full bg-fuchsia-500 rounded-full"
                              style={{
                                width: `${Math.min(100, (used / budget) * 100)}%`,
                              }}
                            />
                          </div>
                        )}
                        {avgMin != null && (
                          <div className="flex justify-between">
                            <span className="text-slate-500">
                              Avg render / video
                            </span>
                            <b className="tabular-nums">~{avgMin} min GPU</b>
                          </div>
                        )}
                        {costs.gpu_hourly_rate ? (
                          <div className="flex justify-between">
                            <span className="text-slate-500">GPU rate</span>
                            <b className="tabular-nums">
                              ${Number(costs.gpu_hourly_rate).toFixed(2)}/hr
                            </b>
                          </div>
                        ) : null}
                      </>
                    );
                  })()}
                </div>
              </div>
              {costs?.quota?.exceeded && (
                <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2 leading-relaxed">
                  {costs.quota.exceeded}. Rendering is paused until the budget
                  resets or is raised.
                </p>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 mt-4 border-t border-slate-100">
            <button
              onClick={() => setProjectForm(null)}
              className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg"
            >
              Cancel
            </button>
            <button
              onClick={saveProject}
              className="flex items-center gap-1.5 px-4 py-2 bg-fuchsia-600 text-white text-sm font-medium rounded-lg hover:bg-fuchsia-700"
            >
              Create & build shot list
            </button>
          </div>
        </PodcastModal>
      )}
      {avatarForm !== null && (
        <VideoAvatarForm
          facilityId={fid}
          initial={avatarForm.id ? avatarForm : null}
          onClose={() => setAvatarForm(null)}
          onSaved={() => {
            setAvatarForm(null);
            refreshAvatars();
          }}
        />
      )}
      {shotListView && (
        <VideoShotListModal
          project={shotListView}
          avatars={avatars}
          onClose={() => setShotListView(null)}
          onApproved={() => {
            setShotListView(null);
            refreshProjects();
          }}
          onSaved={refreshProjects}
          onPreview={
            isAdmin && shotListView.status === "shotlist_ready"
              ? async () => {
                  await runAction(
                    `preview-${shotListView.id}`,
                    `/video/projects/${shotListView.id}/preview`,
                    "Rendering keyframes — the shot list stays open for review.",
                  );
                  setShotListView(null);
                }
              : null
          }
        />
      )}
      {shareData && (
        <SocialShareDialog
          facility={facility}
          content={{
            content_id: `video-${shareData.project.id}`,
            kind: "video",
            title: shareData.project.title || shareData.project.topic || "",
            body: shareData.project.title || shareData.project.topic || "",
            media: [
              {
                url: shareData.url,
                mime_type: "video/mp4",
                kind: "video",
              },
            ],
          }}
          defaultBody={shareData.project.title || shareData.project.topic || ""}
          onClose={() => setShareData(null)}
        />
      )}
    </div>
  );
};

// Podcast → Video bridge: build a promo-clip project from an episode's
// script + existing audio (no re-synthesis). Gated on the video service.
